<template>
    <v-overlay
        :model-value="internalVisible"
        @update:model-value="(val) => $emit('update:modelValue', val)"
        class="align-center justify-center"
        @click:outside="$emit('update:modelValue', false)"
    >
        <v-alert max-width="400px" class="mb-4" color="info">
            <div>
                <p v-if="description">{{ description }}</p>
                <div v-if="delivery_description && (mode === 'reevaluate' || mode === 'partial-approved')" class="mt-4">
                    <p class="mb-1"><strong>Descriere:</strong></p>

                    <div
                        class="delivery-description"
                        :class="{ expanded: expanded }"
                        @click="expanded = !expanded"
                    >
                        {{ delivery_description }}
                    </div>

                    <div class="text-center mt-2" v-if="shouldShowToggle">
                        <v-btn
                            variant="text"
                            size="small"
                            color="primary"
                            @click.stop="expanded = !expanded"
                        >
                            {{ expanded ? "Ascunde" : "Vezi tot" }}
                        </v-btn>
                    </div>
                </div>
                <div v-if="suggested_delivery_date && (mode === 'reevaluate' || mode === 'partial-approved')" class="mt-4">
                    <p class="mb-1"><strong>Sugestie livrare integrala:</strong></p>
                    <p>{{ new Date(suggested_delivery_date ).toLocaleDateString("ro-RO")}}</p>

                </div>

            </div>

            <div
                class="d-flex align-center mt-4"
                v-if="mode !== 'reevaluate' && mode !== 'partial-approved'"
            >
                <v-menu
                    v-model="internalMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    max-width="290"
                    min-width="290"
                >
                    <template #activator="{ props }">
                        <v-text-field
                            v-bind="props"
                            v-model="formattedDate"
                            :label="dateLabel"
                            readonly
                            hide-details
                            density="comfortable"
                            prepend-inner-icon="mdi-calendar"
                        />
                    </template>

                    <v-date-picker
                        v-model="selectedDate"
                        :readonly="mode === 'reevaluate'"
                        @update:model-value="updateDate"
                        color="primary"
                        locale="ro-ro"
                        show-adjacent-months
                        :min="minDate"
                    />
                </v-menu>
            </div>
            <div class="mt-2">
                <template v-if="mode !== 'reevaluate' && mode !== 'partial-approved'">
                    <div class="mt-4 d-flex justify-space-between">
                        <v-btn
                            class="text-green"
                            color="white"
                            @click="confirm"
                            @keyup.enter="confirm"
                            tabindex="0"
                            >Trimite
                        </v-btn>
                        <v-btn
                            class="text-red"
                            color="white"
                            @click="$emit('update:modelValue', false)"
                            >Anulează</v-btn
                        >
                    </div>
                </template>
            </div>

            <div class="mt-2">
                <template
                    v-if="mode === 'reevaluate' || mode === 'partial-approved'"
                >
                    <div class="mt-4 d-flex justify-space-between">
                        <v-btn
                            class="text-green"
                            color="white"
                            @click="approve"
                        >
                            Aprobat
                        </v-btn>
                        <v-btn
                            class="text-red"
                            color="white"
                            @click="$emit('reject-date')"
                        >
                            Respins
                        </v-btn>
                    </div>
                </template>
            </div>
        </v-alert>
    </v-overlay>
</template>

<script>
/**
 * Reusable delivery request dialog that exposes its visibility via v-model,
 * and emits confirm with selected date.
 *
 * @component
 */
export default {
    name: "DeliveryRequestDialog",

    props: {
        modelValue: Boolean,
        description: String,
        dateLabel: String,
        confirmButtonText: String,
        cancelButtonText: String,
        mode: { type: String, default: "create" }, // create | reevaluate
        initialDate: { type: String, default: null },
        delivery_description: String,
        suggested_delivery_date:String

    },
    data() {
        return {
            minDate: new Date().toISOString().split('T')[0], // format YYYY-MM-DD
            internalMenu: false,
            selectedDate: null,
            formattedDate: "",
            expanded: false,
        };
    },
    mounted() {
        if (this.mode === "reevaluate" && this.initialDate) {
            this.selectedDate = this.initialDate;
            this.formattedDate = new Date(this.initialDate).toLocaleDateString(
                "ro-RO"
            );
        }
    },

    computed: {
        shouldShowToggle() {
            return this.delivery_description?.length > 80;
        },
        /**
         * Proxy value for v-model binding to v-overlay
         *
         * @returns {boolean}
         */
        internalVisible() {
            return this.modelValue;
        },
    },
    methods: {
        /**
         * Update selected date and formatted string.
         *
         * @param {string} date
         */
        updateDate(date) {
            this.selectedDate = date;
            this.formattedDate = new Date(date).toLocaleDateString("ro-RO");
            this.internalMenu = false;
        },
        /**
         * Emits a confirmation event with the selected date in ISO format (YYYY-MM-DD).
         * If the selected date is a string, it's trimmed. If it's a Date object, it's
         * converted to ISO format manually (without timezone offset).
         * Also emits an event to close the dialog.
         */
        confirm() {
            if (!this.selectedDate) return;

            let isoDate = "";

            if (typeof this.selectedDate === "string") {
                // deja e în format corect din picker
                isoDate = this.selectedDate.slice(0, 10);
            } else if (this.selectedDate instanceof Date) {
                // eliminăm efectul timezone-ului
                isoDate =
                    this.selectedDate.getFullYear() +
                    "-" +
                    String(this.selectedDate.getMonth() + 1).padStart(2, "0") +
                    "-" +
                    String(this.selectedDate.getDate()).padStart(2, "0");
            }

            this.$emit("confirm", isoDate);
            this.$emit("update:modelValue", false);
        },
        /**
         * Emits an approval event with the selected or initial date in ISO format.
         * If no date is selected or available, the method exits.
         * Also emits an event to close the dialog.
         */
        approve() {
            const dateToSend = this.selectedDate || this.initialDate;
            if (!dateToSend) return;

            this.$emit("approve", this.toISO(dateToSend));
            this.$emit("update:modelValue", false);
        },
        /**
         * Converts a given date (string or Date object) to ISO format (YYYY-MM-DD).
         *
         * @param {string | Date} date - The input date to convert.
         * @returns {string} ISO formatted date string (YYYY-MM-DD).
         */
        toISO(date) {
            if (typeof date === "string") return date.slice(0, 10);
            return `${date.getFullYear()}-${String(
                date.getMonth() + 1
            ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        },
    },
};
</script>
<style scoped>
.delivery-description {
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow: hidden;
    max-height: 4.5rem;
    transition: max-height 0.3s ease;
    cursor: pointer;
}

.delivery-description.expanded {
    max-height: 500px;
}
</style>
